import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
import type { LexiconSet } from '@polyglot/engine';
import { scoreRound } from '@polyglot/engine';
import { Spinner } from '../components/ui';
import { ResultsView } from '../game/Results';
import { currentLocalSession, startLocalSession } from '../game/localSession';
import { loadLexicons } from '../lib/lexicons';
import { formatScore } from '../lib/format';
import { getSettings, updateSettings } from '../lib/settings';
import { InstallHint } from './InstallHint';

export function LocalResults() {
  const { t, i18n } = useTranslation();
  const [, navigate] = useLocation();
  const session = currentLocalSession();
  const [lexicons, setLexicons] = useState<LexiconSet | undefined>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!session) return;
    if (!getSettings().firstGameDone) updateSettings({ firstGameDone: true });
    loadLexicons(session.setup.languages, session.drawInfo.lexiconVersions)
      .then(setLexicons)
      .catch(() => setLexicons(undefined));
  }, [session]);

  if (!session || session.player.status !== 'finished') {
    return (
      <main className="page">
        <p>{t('play.noGame')}</p>
        <Link className="btn primary" href="/setup">
          {t('home.newGame')}
        </Link>
      </main>
    );
  }

  const replay = async () => {
    setBusy(true);
    try {
      await startLocalSession(session.setup);
      navigate('/play');
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    // Shareable result card without solutions (no words, no letters).
    const s = scoreRound(session.player);
    const text = t('results.shareText', {
      mode: t(`modes.${session.setup.mode}.name`),
      langs: session.setup.languages.map((l) => l.toUpperCase()).join('+'),
      score: formatScore(s.totalCents, i18n.language),
      words: session.player.words.length,
    });
    try {
      if (navigator.share) await navigator.share({ text, url: location.origin });
      else await navigator.clipboard.writeText(`${text} ${location.origin}`);
    } catch {
      /* user cancelled */
    }
  };

  return (
    <main className="page">
      <ResultsView
        player={session.player}
        opponent={
          session.botFinal && session.setup.bot
            ? {
                label: t('bots.robotName', { level: t(`bots.${session.setup.bot.level}`) }),
                isBot: true,
                state: session.botFinal,
              }
            : null
        }
        lexicons={lexicons}
        showSolutions
        actions={
          <>
            {busy ? (
              <Spinner label={t('setup.loading')} />
            ) : (
              <button className="btn primary big" onClick={() => void replay()}>
                {t('results.playAgain')}
              </button>
            )}
            <button className="btn ghost" onClick={() => void share()}>
              {t('results.share')}
            </button>
            <Link className="btn ghost" href={`/setup/${session.setup.mode}`}>
              {t('results.changeSetup')}
            </Link>
            <Link className="btn ghost" href="/">
              {t('nav.home')}
            </Link>
          </>
        }
      />
      <p className="muted small">
        {t('results.localNotice')} <Link href="/me">{t('results.savedToHistory')}</Link> ·{' '}
        {t('results.drawAudit', { seed: session.seed, gen: session.drawInfo.generatorVersion })}
      </p>
      <InstallHint />
    </main>
  );
}
